import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  FormHelperText,
  IconButton,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import {
  LoadOrganizationUsersDocument,
  useSetRolesMutation,
} from "~/operations";
import { LoadingButton } from "~/components/loading-button";
import {
  EMAIL_PATTERN,
  HelperText,
} from "~/pages/integrations/utils/integrationValidationUtils";
import { Controller, useForm } from "react-hook-form";
import {
  Close as CloseIcon,
  PersonAdd as PersonAddIcon,
} from "@mui/icons-material";
import { useScope } from "~/hooks/useScope";
import { Fragment, useState } from "react";
import { Add as AddIcon } from "@mui/icons-material";
import { SelectMenu } from "~/components/Form/components/SelectMenu/SelectMenu";
import { MembersRolesList } from "~/components/Members/MembersRolesList";
import {
  BASE_MEMBERS_ROLES,
  GROUPED_MEMBERS_ROLES,
  PERMISSIONS,
} from "~/components/Members/const";
import { hasAnyBaseRoleSelected } from "~/components/Members/utils/membersUtils";
import { roleViewer } from "~/lib/iam/roles/Viewer";

export type AddUserDialogProps = {
  resourceMrn: string;
  onClose?: () => void;
  dialogProps: DialogProps;
};

type FormValues = {
  userEmail: string;
  selectedRoles: string[];
  allowApproveExceptions: boolean;
};

/**
 * Dialog for adding users to an organization.
 * Used in the UsersListPage for organization-level user management.
 */
export function AddUserDialog({
  resourceMrn,
  onClose,
  dialogProps,
}: AddUserDialogProps) {
  const { organizationScope } = useScope();
  const { enqueueSnackbar } = useSnackbar();
  const isSpaceResource = resourceMrn.includes(
    "//captain.api.mondoo.app/spaces/",
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLButtonElement>(null);
  const [isRolesDropdownOpen, setIsRolesDropdownOpen] = useState(false);

  const [setRoles, { loading }] = useSetRolesMutation({
    refetchQueries: [LoadOrganizationUsersDocument],
    awaitRefetchQueries: true,
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
    setValue,
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      userEmail: "",
      selectedRoles: [roleViewer.mrn],
      allowApproveExceptions: false,
    },
    mode: "onChange",
  });

  const watchSelectedRoles = watch("selectedRoles");

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const onAddMember = handleSubmit(async (data: FormValues) => {
    const userEmail = data.userEmail;
    const roles = data.selectedRoles;

    try {
      if (!resourceMrn) throw new Error("resourceMrn is required");

      const result = await setRoles({
        variables: {
          input: {
            identity: userEmail,
            roles,
            scopeMrn: resourceMrn,
          },
        },
      });

      const errors = result.data?.setRoles?.errors;
      if (errors && errors.length > 0) {
        throw new Error(errors.join(", "));
      }

      enqueueSnackbar("User successfully added", {
        variant: "success",
      });
    } catch (error) {
      let errorMsg = "Failed to add user";

      if (String(error).includes("permission")) {
        errorMsg = "You do not have permission to add a user.";
      } else if (String(error).includes("AlreadyExists")) {
        errorMsg = "This user is already a member";
      } else if (String(error).includes("rate limit")) {
        errorMsg = "Rate limit exceeded. Please try again in a few seconds.";
      }

      enqueueSnackbar(errorMsg, {
        variant: "error",
      });
    } finally {
      handleClose();
    }
  });

  return (
    <Dialog
      id="add-user-dialog"
      scroll="paper"
      onClose={handleClose}
      {...dialogProps}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            bgcolor: "background.default",
            position: "relative",
            overflow: "visible",
          },
        },
      }}
    >
      <DialogTitle
        component="div"
        id="add-user-dialog-title"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "background.default",
          p: 5,
          pb: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <PersonAddIcon sx={{ mr: 1.5 }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: "700",
              textTransform: "uppercase",
            }}
          >
            ADD USER
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{ bgcolor: "background.default", p: 5, overflow: "visible" }}
      >
        {isSpaceResource && (
          <Typography variant="body2" color="text.secondary" mt={2}>
            You can add members to your entire organization or individual
            spaces. Add members to your space here. Manage organization-wide
            permissions from the{" "}
            <Link
              href={`/organization/settings/members?organizationId=${organizationScope?.id}`}
              underline="always"
            >
              Organization's Settings page
            </Link>
            .
          </Typography>
        )}

        <form onSubmit={onAddMember}>
          <Typography variant="body2" fontWeight="medium" sx={{ mb: 1, mt: 3 }}>
            User email
          </Typography>
          <Controller
            name="userEmail"
            control={control}
            rules={{
              required: "Email is required",
              pattern: {
                value: EMAIL_PATTERN,
                message: "Please enter a valid email address",
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                placeholder="user@example.com"
                fullWidth
                autoFocus
                error={!!errors.userEmail}
                helperText={errors.userEmail?.message || ""}
                sx={{
                  mb: 3,
                  bgcolor: "background.paper",
                  ".MuiOutlinedInput-notchedOutline": {
                    borderColor: "divider",
                  },
                }}
              />
            )}
          />

          <Button
            variant="text"
            disabled={false}
            color="primary"
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              setAnchorEl(event.currentTarget);
              setIsRolesDropdownOpen(true);
            }}
            startIcon={<AddIcon />}
            data-testid="add-roles-button"
          >
            Add Roles
          </Button>

          <Controller
            name="selectedRoles"
            control={control}
            rules={{
              validate: (value) => {
                const isHasAnyBaseRoleSelected = hasAnyBaseRoleSelected(value);
                if (!isHasAnyBaseRoleSelected) {
                  return "Must have at least one base role selected";
                }
                return true;
              },
            }}
            render={({ field, formState: { errors } }) => (
              <Box sx={{ position: "relative" }}>
                <SelectMenu
                  isOpen={isRolesDropdownOpen}
                  anchorRef={anchorEl}
                  onClose={() => {
                    setAnchorEl(null);
                    setIsRolesDropdownOpen(false);
                  }}
                  maxHeight={208}
                  options={GROUPED_MEMBERS_ROLES}
                  onChange={(value) => {
                    field.onChange(value);
                  }}
                  multiple={true}
                  value={field.value}
                  variant="checkbox"
                  error={!!errors.selectedRoles}
                  helperText={errors.selectedRoles?.message || ""}
                />
              </Box>
            )}
          />

          <Fragment>
            <FormHelperText error>
              {errors.selectedRoles?.message || ""}
            </FormHelperText>
            <Box pl={1}>
              <MembersRolesList
                roles={watchSelectedRoles.map((roleMrn) => {
                  const label = [...BASE_MEMBERS_ROLES, ...PERMISSIONS].find(
                    (role) => role.value === roleMrn,
                  )?.label;

                  return {
                    value: roleMrn,
                    label: label || "",
                  };
                })}
                onDelete={(roleMrn) =>
                  setValue(
                    "selectedRoles",
                    watchSelectedRoles.filter((x) => x !== roleMrn),
                    { shouldValidate: true },
                  )
                }
              />
            </Box>
          </Fragment>
        </form>
      </DialogContent>
      <DialogActions
        sx={{
          position: "sticky",
          justifyContent: "center",
          alignItems: "start",
          bottom: 0,
          left: 0,
          height: (theme) => theme.spacing(10.5),
          bgcolor: "background.default",
        }}
      >
        <LoadingButton
          variant="contained"
          color="primary"
          onClick={onAddMember}
          loading={loading}
          data-name="add-user"
          buttonText="Add User"
          disabled={!isValid}
          sx={{ borderRadius: 1 }}
        />
      </DialogActions>
    </Dialog>
  );
}
